import type { DatabaseService } from '@core';
import type {
  EmptyPayloadExtender,
  GenericRepository,
} from '@core/ports/repositories/generic-repository';

export const getGenericRepository = <
  TEntity,
  TEntityData,
  TPayloadExtender = EmptyPayloadExtender,
>(
  getDatabaseCollection: (payload: TPayloadExtender) => string,
  databaseService: DatabaseService,
): GenericRepository<TEntity, TEntityData, TPayloadExtender> => ({
  async get(payload) {
    return databaseService.get<TEntity>(getDatabaseCollection(payload), payload.id);
  },

  async getAll(payload) {
    return databaseService.getPaginated<TEntity>(
      getDatabaseCollection(payload),
      payload.queryConstraints ?? [],
      payload.pagination ?? { limit: 10 },
      payload.orderBy,
    );
  },

  async create(payload) {
    return databaseService.create<TEntityData>(
      getDatabaseCollection(payload),
      payload.data,
    );
  },

  async set(payload) {
    return databaseService.set<TEntityData>(
      getDatabaseCollection(payload),
      payload.id,
      payload.data,
    );
  },

  async update(payload) {
    return databaseService.update<TEntityData>(
      getDatabaseCollection(payload),
      payload.id,
      payload.data,
    );
  },

  async delete(payload) {
    return databaseService.delete(getDatabaseCollection(payload), payload.id);
  },

  async addToSet(payload) {
    return databaseService.addToSet(
      getDatabaseCollection(payload),
      payload.id,
      String(payload.fieldName),
      payload.value as unknown,
    );
  },

  async removeFromSet(payload) {
    return databaseService.removeFromSet(
      getDatabaseCollection(payload),
      payload.id,
      String(payload.fieldName),
      payload.value as unknown,
    );
  },

  subscribe(payload) {
    return databaseService.subscribe<TEntity>(
      getDatabaseCollection(payload),
      payload.id,
      payload.callback,
    );
  },
});
